import { RouteProp, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	DuelBreakdownBannerJpg,
	DuelCalendarPng,
	DuelChipsJpg,
	DuelIconCardsSvg,
	DuelIconClockSvg,
	DuelIconPlayersSvg,
	DuelScoreCardsSvg,
	DuelScoreFlameSvg,
	DuelScoreStarSvg,
} from '../../assets/duels';
import {
	duelResultBlur,
	duelResultFocus,
	duelResultRetry,
	duelsScreenMounted,
	duelsScreenUnmounted,
} from '../../store/duels/duelsActions';
import { selectDuelResult, selectDuelResultErrorKey } from '../../store/duels/duelsReducer';
import { GoldGradientText } from '../Components/common/GoldGradientText';
import { duelText } from '../Components/Duels/duelsText';
import {
	deltaColor,
	durationParts,
	formatScore,
	formatSettledAt,
	formatSigned,
} from '../Components/Duels/duelsTheme';
import {
	DuelLoading,
	DuelNotice,
	DuelPanel,
	DuelsPage,
	duelTextStyles,
} from '../Components/Duels/DuelsUi';
import { RootStackParamList } from '../navigator';

type NavType = NativeStackNavigationProp<RootStackParamList>;
type BreakdownRoute = RouteProp<RootStackParamList, 'DuelScoreBreakdown'>;

const SOURCES = [
	{
		key: 'outcome',
		title: 'T_SITE_DUELS_SOURCE_OUTCOME',
		body: 'T_SITE_DUELS_SOURCE_OUTCOME_BODY',
	},
	{
		key: 'decisionQuality',
		title: 'T_SITE_DUELS_SOURCE_DECISIONS',
		body: 'T_SITE_DUELS_SOURCE_DECISIONS_BODY',
	},
	{
		key: 'riskManagement',
		title: 'T_SITE_DUELS_SOURCE_RISK',
		body: 'T_SITE_DUELS_SOURCE_RISK_BODY',
	},
	{ key: 'timeBonus', title: 'T_SITE_DUELS_SOURCE_TIME', body: 'T_SITE_DUELS_SOURCE_TIME_BODY' },
] as const;

const SourceIcon = ({
	source,
	size,
}: {
	source: (typeof SOURCES)[number]['key'];
	size: number;
}) => {
	switch (source) {
		case 'outcome':
			return (
				<Image source={DuelChipsJpg} style={{ width: size, height: size }} resizeMode='contain' />
			);
		case 'decisionQuality':
			return <DuelScoreStarSvg width={size} height={size} />;
		case 'riskManagement':
			return <DuelScoreCardsSvg width={size} height={size} />;
		default:
			return <DuelScoreFlameSvg width={size} height={size} />;
	}
};

const InfoRow = ({
	icon,
	label,
	value,
	scale,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
	scale: number;
}) => (
	<View style={[styles.row, { columnGap: 10 * scale }]}>
		{icon}
		<Text style={[duelTextStyles.body, styles.flex, { fontSize: 14 * scale }]}>{label}</Text>
		<Text style={[duelTextStyles.body, { fontSize: 14 * scale }]}>{value}</Text>
	</View>
);

const DuelScoreBreakdownScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();
	const { duelId } = useRoute<BreakdownRoute>().params;
	const result = useSelector(selectDuelResult(duelId));
	const errorKey = useSelector(selectDuelResultErrorKey(duelId));

	useEffect(() => {
		dispatch(duelsScreenMounted());
		return () => {
			dispatch(duelsScreenUnmounted());
		};
	}, [dispatch]);

	useFocusEffect(
		useCallback(() => {
			dispatch(duelResultFocus(duelId));
			return () => {
				dispatch(duelResultBlur(duelId));
			};
		}, [dispatch, duelId]),
	);

	return (
		<DuelsPage
			title={duelText(t, 'T_SITE_DUELS_BREAKDOWN_TITLE')}
			onBack={() => navigation.goBack()}
		>
			{({ width, scale }) => {
				if (!result) {
					return errorKey ? (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(t, errorKey)}
							actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
							onAction={() => dispatch(duelResultRetry(duelId))}
						/>
					) : (
						<DuelLoading />
					);
				}

				const breakdown = result.breakdown;
				const duration = result.runDurationMs !== null ? durationParts(result.runDurationMs) : null;
				const iconSize = 22 * scale;
				const infoRows = [
					duration
						? {
								key: 'duration',
								icon: <DuelIconClockSvg width={iconSize} height={iconSize} />,
								label: duelText(t, 'T_SITE_DUELS_RUN_DURATION'),
								value: duelText(t, 'T_SITE_DUELS_DURATION', [duration.minutes, duration.seconds]),
							}
						: null,
					result.handsPlayed !== null
						? {
								key: 'hands',
								icon: <DuelIconCardsSvg width={iconSize} height={iconSize} />,
								label: duelText(t, 'T_SITE_DUELS_HANDS_PLAYED'),
								value: String(result.handsPlayed),
							}
						: null,
					{
						key: 'players',
						icon: <DuelIconPlayersSvg width={iconSize} height={iconSize} />,
						label: duelText(t, 'T_SITE_DUELS_PLAYERS_IN_DUEL'),
						value: String(result.participantCount),
					},
					result.settledAt
						? {
								key: 'completed',
								icon: (
									<Image
										source={DuelCalendarPng}
										style={{ width: iconSize, height: iconSize }}
										resizeMode='contain'
									/>
								),
								label: duelText(t, 'T_SITE_DUELS_COMPLETED_AT'),
								value: formatSettledAt(result.settledAt, ' '),
							}
						: null,
				].filter(<T,>(row: T | null): row is T => row !== null);

				return (
					<>
						<DuelPanel scale={scale} width={width} highlighted style={styles.flush}>
							<View
								style={[
									styles.banner,
									{ height: (width * 363) / 1080 + 16 * scale, borderRadius: 12 * scale },
								]}
							>
								<Image
									source={DuelBreakdownBannerJpg}
									style={styles.bannerImage}
									resizeMode='cover'
								/>
								<View style={[styles.bannerText, { width: width * 0.55, paddingRight: 8 * scale }]}>
									<Text style={[duelTextStyles.title, { fontSize: 18 * scale }]}>
										{duelText(t, 'T_SITE_DUELS_FINAL_SCORE')}
									</Text>
									<GoldGradientText
										text={formatScore(result.displayScore)}
										width={width * 0.55 - 8 * scale}
										height={48 * scale}
										fontSize={42 * scale}
										fontWeight='900'
									/>
									<Text style={[duelTextStyles.body, { fontSize: 12 * scale }]}>
										{`${duelText(t, 'T_SITE_DUELS_FINAL_POSITION')} ${result.rank ? `#${result.rank}` : '—'} ${duelText(t, 'T_SITE_DUELS_POSITION_OF', [result.eligibleCount])}`}
									</Text>
								</View>
							</View>
						</DuelPanel>

						{breakdown ? (
							<DuelPanel scale={scale} width={width}>
								<Text style={[duelTextStyles.gold, { fontSize: 16 * scale }]}>
									{duelText(t, 'T_SITE_DUELS_SCORE_SOURCES')}
								</Text>
								{SOURCES.map(source => (
									<View
										key={source.key}
										style={[
											styles.row,
											styles.source,
											{ columnGap: 10 * scale, padding: 10 * scale, borderRadius: 8 * scale },
										]}
									>
										<SourceIcon source={source.key} size={26 * scale} />
										<View style={styles.flex}>
											<Text style={[duelTextStyles.title, { fontSize: 15 * scale }]}>
												{duelText(t, source.title)}
											</Text>
											<Text style={[duelTextStyles.muted, { fontSize: 11 * scale }]}>
												{duelText(t, source.body)}
											</Text>
										</View>
										<Text
											style={[
												duelTextStyles.title,
												{ fontSize: 16 * scale, color: deltaColor(breakdown[source.key]) },
											]}
										>
											{formatSigned(breakdown[source.key])}
										</Text>
									</View>
								))}
								<View
									style={[
										styles.row,
										styles.between,
										styles.total,
										{ padding: 10 * scale, borderRadius: 8 * scale },
									]}
								>
									<Text style={[duelTextStyles.gold, { fontSize: 16 * scale }]}>
										{duelText(t, 'T_SITE_DUELS_TOTAL_SCORE')}
									</Text>
									<Text style={[duelTextStyles.gold, { fontSize: 20 * scale }]}>
										{formatScore(breakdown.total)}
									</Text>
								</View>
							</DuelPanel>
						) : null}

						<DuelPanel scale={scale} width={width}>
							<Text style={[duelTextStyles.gold, { fontSize: 16 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_ADDITIONAL_INFO')}
							</Text>
							{infoRows.map(row => (
								<InfoRow
									key={row.key}
									icon={row.icon}
									label={row.label}
									value={row.value}
									scale={scale}
								/>
							))}
						</DuelPanel>
					</>
				);
			}}
		</DuelsPage>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	between: {
		justifyContent: 'space-between',
	},
	flex: {
		flex: 1,
		minWidth: 0,
	},
	source: {
		backgroundColor: 'rgba(0,0,0,0.28)',
	},
	total: {
		borderWidth: 1,
		borderColor: '#E08712',
	},
	flush: {
		padding: 0,
	},
	banner: {
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'flex-end',
	},
	bannerImage: {
		...StyleSheet.absoluteFillObject,
		width: undefined,
		height: undefined,
	},
	bannerText: {
		alignItems: 'center',
	},
});

export default DuelScoreBreakdownScreen;
